<script>
import Loader from '@/components/widgets/loader.vue';
import { notify } from '@/helpers/notify';

export default {
  name: 'DeviceDetailModal',
  components: { Loader },
  props: {
    modelOpen: { type: [Number, String], default: 0 },
    device: { type: Object, default: () => ({}) },
    router: { type: Object, default: null },
    endpoint: { type: String, default: '/routers/details' },
    title: { type: String, default: 'Cihaz Detay' },
    modalId: { type: String, default: 'device_detail_modal' },
    showWireless: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      clientsLoading: false,
      logsLoading: false,
      trafficLoading: false,
      details: null,
      errorMessage: '',
      activeTab: 'system',
      liveLogs: false,
      liveTraffic: false,
      logTimer: null,
      trafficTimer: null,
    };
  },
  computed: {
    currentDevice() {
      if (this.device && this.device.id) return this.device;
      if (this.router && this.router.id) {
        return {
          id: this.router.id,
          title: this.router.shortname,
          subtitle: this.router.nasname,
        };
      }
      return {};
    },
    headerTitle() {
      return this.currentDevice.title || this.currentDevice.name || '-';
    },
    headerSubtitle() {
      return this.currentDevice.subtitle || this.currentDevice.host || this.currentDevice.ip || '';
    },
    hasWireless() {
      return this.showWireless || !!(this.details && this.details.has_wireless);
    },
    clients() {
      return (this.details && Array.isArray(this.details.wireless_clients))
        ? this.details.wireless_clients
        : [];
    },
    logs() {
      return (this.details && Array.isArray(this.details.logs))
        ? this.details.logs
        : [];
    },
    sectors() {
      return (this.details && Array.isArray(this.details.sectors))
        ? this.details.sectors
        : [];
    },
    traffic() {
      return (this.details && this.details.traffic) ? this.details.traffic : null;
    },
    trafficInterfaces() {
      return (this.traffic && Array.isArray(this.traffic.interfaces))
        ? this.traffic.interfaces
        : [];
    },
    matchedCount() {
      if (this.details && this.details.matched_count != null) {
        return this.details.matched_count;
      }
      return this.clients.filter((c) => c.matched).length;
    },
    modalSize() {
      return this.hasWireless ? 'xl' : 'lg';
    },
  },
  watch: {
    modelOpen() {
      this.details = null;
      this.errorMessage = '';
      this.activeTab = 'system';
      this.stopLogPoll();
      this.stopTrafficPoll();
      this.fetchDetails();
    },
    activeTab(val) {
      if (val === 'logs' && this.liveLogs) {
        this.startLogPoll();
      } else {
        this.stopLogPoll();
      }

      if (val === 'system' && this.liveTraffic && this.showWireless) {
        this.startTrafficPoll();
      } else if (val !== 'system') {
        this.stopTrafficPoll();
      }
    },
    liveLogs(val) {
      if (val && this.activeTab === 'logs') {
        this.startLogPoll();
      } else {
        this.stopLogPoll();
      }
    },
    liveTraffic(val) {
      if (val && this.activeTab === 'system' && this.showWireless) {
        this.startTrafficPoll();
      } else {
        this.stopTrafficPoll();
      }
    },
  },
  beforeDestroy() {
    this.stopLogPoll();
    this.stopTrafficPoll();
  },
  methods: {
    usageClass(percent) {
      const value = Number(percent) || 0;
      if (value >= 85) return 'is-danger';
      if (value >= 70) return 'is-warning';
      return 'is-success';
    },

    signalClass(dbm) {
      if (dbm === null || dbm === undefined || dbm === '') return '';
      const v = Number(dbm);
      if (Number.isNaN(v)) return '';
      if (v >= -55) return 'is-success';
      if (v >= -70) return 'is-warning';
      return 'is-danger';
    },

    snrClass(snr) {
      const v = parseInt(snr, 10);
      if (Number.isNaN(v)) return '';
      if (v >= 30) return 'is-success';
      if (v >= 20) return 'is-warning';
      return 'is-danger';
    },

    clientName(row) {
      if (!row.subscription) return 'Eşleşmedi';
      return row.subscription.customer_name || row.subscription.username || '-';
    },

    fetchDetails() {
      if (!this.currentDevice.id) return;

      this.loading = true;
      this.$http.post(this.endpoint, { id: this.currentDevice.id })
        .then(response => {
          if (response.data.error) {
            this.errorMessage = response.data.message || 'Detay alınamadı';
            this.details = null;
            return notify.error(this.errorMessage);
          }
          this.details = response.data.content || null;
          if (this.hasWireless && this.activeTab === 'logs' && this.liveLogs) {
            this.startLogPoll();
          }
          // Trafik otomatik çekilmez; kullanıcı açınca / Yenile ile gelir
        })
        .catch(() => {
          this.errorMessage = 'Cihaz detayı alınırken hata oluştu';
          this.details = null;
          notify.error(this.errorMessage);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    refreshClients() {
      if (!this.currentDevice.id || !this.showWireless) {
        return this.fetchDetails();
      }

      this.clientsLoading = true;
      this.$http.post('/transmitter/wireless-clients', { id: this.currentDevice.id })
        .then((response) => {
          if (response.data.error) {
            return notify.error(response.data.message || 'Kullanıcı listesi alınamadı');
          }
          const content = response.data.content || {};
          if (!this.details) this.details = {};
          this.$set(this.details, 'wireless_clients', content.wireless_clients || []);
          this.$set(this.details, 'wireless_count', content.wireless_count || 0);
          this.$set(this.details, 'matched_count', content.matched_count || 0);
          this.$set(this.details, 'sectors', content.sectors || []);
          this.$set(this.details, 'has_wireless', true);
        })
        .catch(() => notify.error('Kullanıcı listesi alınamadı'))
        .finally(() => {
          this.clientsLoading = false;
        });
    },

    refreshTraffic(silent = false) {
      if (!this.currentDevice.id || !this.showWireless) return;

      if (!silent) this.trafficLoading = true;
      this.$http.post('/transmitter/traffic', { id: this.currentDevice.id })
        .then((response) => {
          if (response.data.error) {
            if (!silent) notify.error(response.data.message || 'Trafik alınamadı');
            return;
          }
          const content = response.data.content || {};
          if (!this.details) this.details = {};
          this.$set(this.details, 'traffic', content.traffic || null);
        })
        .catch(() => {
          if (!silent) notify.error('Trafik alınamadı');
        })
        .finally(() => {
          this.trafficLoading = false;
        });
    },

    refreshLogs(silent = false) {
      if (!this.currentDevice.id || !this.showWireless) return;

      if (!silent) this.logsLoading = true;
      this.$http.post('/transmitter/logs', { id: this.currentDevice.id, limit: 80 })
        .then((response) => {
          if (response.data.error) {
            if (!silent) notify.error(response.data.message || 'Log alınamadı');
            return;
          }
          const content = response.data.content || {};
          if (!this.details) this.details = {};
          this.$set(this.details, 'logs', content.logs || []);
          this.$nextTick(() => this.scrollLogsBottom());
        })
        .catch(() => {
          if (!silent) notify.error('Log alınamadı');
        })
        .finally(() => {
          this.logsLoading = false;
        });
    },

    startLogPoll() {
      this.stopLogPoll();
      if (!this.showWireless) return;
      this.refreshLogs(true);
      this.logTimer = setInterval(() => {
        if (this.activeTab === 'logs' && this.liveLogs) {
          this.refreshLogs(true);
        }
      }, 4000);
    },

    stopLogPoll() {
      if (this.logTimer) {
        clearInterval(this.logTimer);
        this.logTimer = null;
      }
    },

    startTrafficPoll() {
      this.stopTrafficPoll();
      if (!this.showWireless) return;
      this.refreshTraffic(true);
      this.trafficTimer = setInterval(() => {
        if (this.activeTab === 'system' && this.liveTraffic) {
          this.refreshTraffic(true);
        }
      }, 10000);
    },

    stopTrafficPoll() {
      if (this.trafficTimer) {
        clearInterval(this.trafficTimer);
        this.trafficTimer = null;
      }
    },

    scrollLogsBottom() {
      const el = this.$refs.logStream;
      if (el) el.scrollTop = el.scrollHeight;
    },

    close() {
      this.stopLogPoll();
      this.stopTrafficPoll();
      this.$parent.$bvModal.hide(this.modalId);
    },

    onModalHide() {
      this.stopLogPoll();
      this.stopTrafficPoll();
    },
  },
};
</script>

<template>
  <b-modal :id="modalId" :size="modalSize" no-close-on-backdrop hide-footer @hide="onModalHide">
    <template #modal-header>
      <div class="rd-header">
        <div>
          <div class="rd-header__title">
            <i class="bx bx-chip"></i>
            {{ title }}
          </div>
          <div class="rd-header__sub">
            {{ headerTitle }}
            <span v-if="headerSubtitle"> · {{ headerSubtitle }}</span>
          </div>
        </div>
        <div class="rd-header__actions">
          <b-button size="sm" variant="outline-primary" :disabled="loading" @click="fetchDetails">
            <i class="bx bx-refresh"></i> Yenile
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="close">Kapat</b-button>
        </div>
      </div>
    </template>

    <Loader :loading="true" :load="loading">
      <div class="rd-wrap" v-if="details">
        <ul v-if="hasWireless" class="nav nav-tabs rd-tabs mb-3">
          <li class="nav-item">
            <a
              href="#"
              class="nav-link"
              :class="{ active: activeTab === 'system' }"
              @click.prevent="activeTab = 'system'"
            >Sistem</a>
          </li>
          <li class="nav-item">
            <a
              href="#"
              class="nav-link"
              :class="{ active: activeTab === 'clients' }"
              @click.prevent="activeTab = 'clients'"
            >
              Wireless Kullanıcılar
              <span class="badge bg-primary ms-1">{{ details.wireless_count || clients.length }}</span>
            </a>
          </li>
          <li class="nav-item">
            <a
              href="#"
              class="nav-link"
              :class="{ active: activeTab === 'logs' }"
              @click.prevent="activeTab = 'logs'"
            >Canlı Log</a>
          </li>
        </ul>

        <div v-show="activeTab === 'system'" class="row g-3">
          <template v-if="hasWireless">
            <div class="col-12">
              <div class="rd-toolbar mb-1">
                <div class="form-check form-switch mb-0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="rd-live-traffic"
                    v-model="liveTraffic"
                  >
                  <label class="form-check-label" for="rd-live-traffic">Canlı trafik (10 sn, cache’li)</label>
                </div>
                <b-button size="sm" variant="outline-primary" :disabled="trafficLoading" @click="refreshTraffic(false)">
                  <i class="bx bx-refresh"></i> Trafik Yenile
                </b-button>
              </div>
            </div>

            <div class="col-md-4 col-sm-6">
              <div class="rd-card rd-card--traffic">
                <div class="rd-card__label">Toplam RX</div>
                <div class="rd-card__value is-success">↓ {{ traffic && traffic.rx ? traffic.rx : '0 bps' }}</div>
              </div>
            </div>
            <div class="col-md-4 col-sm-6">
              <div class="rd-card rd-card--traffic">
                <div class="rd-card__label">Toplam TX</div>
                <div class="rd-card__value is-primary">↑ {{ traffic && traffic.tx ? traffic.tx : '0 bps' }}</div>
              </div>
            </div>
            <div class="col-md-4 col-sm-6">
              <div class="rd-card rd-card--traffic">
                <div class="rd-card__label">Registration</div>
                <div class="rd-card__value">
                  {{ details.wireless_count || clients.length || 0 }}
                  <span class="rd-card__sub">bağlı</span>
                </div>
              </div>
            </div>

            <div class="col-12" v-if="sectors.length">
              <div class="rd-card">
                <div class="rd-card__label mb-2">Sektör / Interface Adetleri</div>
                <div class="rd-sectors">
                  <div
                    v-for="sector in sectors"
                    :key="sector.interface"
                    class="rd-sector"
                  >
                    <div class="rd-sector__name">{{ sector.interface }}</div>
                    <div class="rd-sector__count">{{ sector.count }}</div>
                    <div class="rd-sector__meta">CRM: {{ sector.matched }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12" v-if="trafficInterfaces.length">
              <div class="table-responsive rd-table-wrap">
                <table class="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Interface</th>
                      <th>RX</th>
                      <th>TX</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in trafficInterfaces" :key="row.interface">
                      <td>{{ row.interface }}</td>
                      <td class="text-success">↓ {{ row.rx }}</td>
                      <td class="text-primary">↑ {{ row.tx }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">Cihaz Model Adı</div>
              <div class="rd-card__value">{{ details.board_name }}</div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">Versiyon</div>
              <div class="rd-card__value">{{ details.version }}</div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">Platform</div>
              <div class="rd-card__value">{{ details.platform }}</div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">Cihaz Mimarisi</div>
              <div class="rd-card__value">{{ details.architecture }}</div>
            </div>
          </div>

          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">CPU</div>
              <div class="rd-card__value">{{ details.cpu }}</div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">CPU Çekirdek Sayısı</div>
              <div class="rd-card__value">{{ details.cpu_count }}</div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">CPU Frekans</div>
              <div class="rd-card__value">{{ details.cpu_frequency }}</div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6">
            <div class="rd-card">
              <div class="rd-card__label">CPU Kullanımı</div>
              <div class="rd-card__value" :class="usageClass(details.cpu_load)">
                %{{ details.cpu_load }}
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="rd-card rd-card--usage">
              <div class="rd-card__label">RAM Kullanımı</div>
              <div class="rd-progress">
                <div
                  class="rd-progress__bar"
                  :class="usageClass(details.memory.percent)"
                  :style="{ width: Math.min(100, details.memory.percent) + '%' }"
                >
                  %{{ details.memory.percent }}
                </div>
              </div>
              <div class="rd-card__meta">
                Boşta: {{ details.memory.free }} | Toplam: {{ details.memory.total }}
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="rd-card rd-card--usage">
              <div class="rd-card__label">Depolama</div>
              <div class="rd-progress">
                <div
                  class="rd-progress__bar"
                  :class="usageClass(details.storage.percent)"
                  :style="{ width: Math.min(100, details.storage.percent) + '%' }"
                >
                  %{{ details.storage.percent }}
                </div>
              </div>
              <div class="rd-card__meta">
                Boşta: {{ details.storage.free }} | Toplam: {{ details.storage.total }}
              </div>
            </div>
          </div>

          <div class="col-12" v-if="details.uptime">
            <div class="rd-card">
              <div class="rd-card__label">Uptime</div>
              <div class="rd-card__value">{{ details.uptime }}</div>
            </div>
          </div>
        </div>

        <div v-if="hasWireless" v-show="activeTab === 'clients'">
          <div class="rd-toolbar mb-2">
            <div class="text-muted small">
              Registration: <strong>{{ details.wireless_count || clients.length }}</strong>
              · CRM eşleşen: <strong>{{ matchedCount }}</strong>
            </div>
            <b-button size="sm" variant="outline-primary" :disabled="clientsLoading" @click="refreshClients">
              <i class="bx bx-refresh"></i> Listeyi Yenile
            </b-button>
          </div>

          <div class="rd-sectors mb-3" v-if="sectors.length">
            <div
              v-for="sector in sectors"
              :key="'c-' + sector.interface"
              class="rd-sector"
            >
              <div class="rd-sector__name">{{ sector.interface }}</div>
              <div class="rd-sector__count">{{ sector.count }}</div>
              <div class="rd-sector__meta">CRM: {{ sector.matched }}</div>
            </div>
          </div>

          <div class="table-responsive rd-table-wrap">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Abone</th>
                  <th>MAC</th>
                  <th>IP</th>
                  <th>Online</th>
                  <th>Çekim</th>
                  <th>SNR</th>
                  <th>CCQ</th>
                  <th>Rate</th>
                  <th>Interface</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!clients.length">
                  <td colspan="9" class="text-center text-muted py-4">Bağlı wireless kullanıcı yok</td>
                </tr>
                <tr v-for="(row, idx) in clients" :key="row.mac + '-' + idx" :class="{ 'table-success': row.matched }">
                  <td>
                    <div class="fw-semibold">{{ clientName(row) }}</div>
                    <div class="small text-muted" v-if="row.subscription">
                      {{ row.subscription.username }}
                    </div>
                    <span v-else class="badge bg-secondary">CRM eşleşmedi</span>
                  </td>
                  <td><code>{{ row.mac || '-' }}</code></td>
                  <td>{{ row.last_ip || '-' }}</td>
                  <td>{{ row.uptime || '-' }}</td>
                  <td>
                    <span :class="signalClass(row.signal_dbm)">{{ row.signal || '-' }}</span>
                  </td>
                  <td>
                    <span :class="snrClass(row.snr)">{{ row.snr || '-' }}</span>
                  </td>
                  <td>
                    <span v-if="row.tx_ccq || row.rx_ccq">
                      TX {{ row.tx_ccq || '-' }} / RX {{ row.rx_ccq || '-' }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="small">
                    <div>↓ {{ row.rx_rate || '-' }}</div>
                    <div>↑ {{ row.tx_rate || '-' }}</div>
                  </td>
                  <td class="small">
                    <div>{{ row.interface || '-' }}</div>
                    <div class="text-muted" v-if="row.radio_name">{{ row.radio_name }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="hasWireless" v-show="activeTab === 'logs'">
          <div class="rd-toolbar mb-2">
            <div class="form-check form-switch mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                id="rd-live-logs"
                v-model="liveLogs"
              >
              <label class="form-check-label" for="rd-live-logs">Canlı akış (4 sn)</label>
            </div>
            <b-button size="sm" variant="outline-primary" :disabled="logsLoading" @click="refreshLogs(false)">
              <i class="bx bx-refresh"></i> Log Yenile
            </b-button>
          </div>

          <div ref="logStream" class="rd-log">
            <div v-if="!logs.length" class="rd-log__empty">Log kaydı yok</div>
            <div
              v-for="(log, idx) in logs"
              :key="idx"
              class="rd-log__line"
            >
              <span class="rd-log__time">{{ log.time || '-' }}</span>
              <span class="rd-log__topics">{{ log.topics || '-' }}</span>
              <span class="rd-log__msg">{{ log.message }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!loading" class="rd-empty">
        <i class="bx bx-error-circle"></i>
        <div>{{ errorMessage || 'Detay bilgisi bulunamadı' }}</div>
      </div>
    </Loader>
  </b-modal>
</template>

<style scoped>
.rd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.rd-header__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.rd-header__title i {
  color: #2563eb;
  font-size: 1.25rem;
}

.rd-header__sub {
  margin-top: 2px;
  font-size: 0.8rem;
  color: #64748b;
}

.rd-header__actions {
  display: flex;
  gap: 8px;
}

.rd-wrap {
  min-height: 180px;
}

.rd-tabs .nav-link {
  font-size: 0.9rem;
  font-weight: 600;
}

.rd-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rd-card {
  height: 100%;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.rd-card__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
}

.rd-card__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
  line-height: 1.35;
}

.rd-card__value.is-success,
.is-success { color: #16a34a; }
.rd-card__value.is-warning,
.is-warning { color: #d97706; }
.rd-card__value.is-danger,
.is-danger { color: #dc2626; }
.rd-card__value.is-primary { color: #2563eb; }

.rd-card__sub {
  display: block;
  margin-top: 2px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.rd-sectors {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.rd-sector {
  min-width: 110px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.rd-sector__name {
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  word-break: break-all;
}

.rd-sector__count {
  margin-top: 4px;
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
}

.rd-sector__meta {
  margin-top: 4px;
  font-size: 0.72rem;
  color: #64748b;
}

.rd-card__meta {
  margin-top: 10px;
  font-size: 0.82rem;
  color: #64748b;
}

.rd-progress {
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  overflow: hidden;
}

.rd-progress__bar {
  height: 100%;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  transition: width 0.35s ease;
}

.rd-progress__bar.is-success { background: #22c55e; }
.rd-progress__bar.is-warning { background: #f59e0b; }
.rd-progress__bar.is-danger { background: #ef4444; }

.rd-table-wrap {
  max-height: 420px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.rd-table-wrap thead th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  z-index: 1;
  font-size: 0.78rem;
  white-space: nowrap;
}

.rd-log {
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 10px;
  height: 360px;
  overflow: auto;
  padding: 12px 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
  line-height: 1.45;
}

.rd-log__empty {
  color: #94a3b8;
  text-align: center;
  padding: 40px 0;
}

.rd-log__line {
  display: grid;
  grid-template-columns: 70px 110px 1fr;
  gap: 10px;
  padding: 2px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
}

.rd-log__time { color: #38bdf8; }
.rd-log__topics { color: #a78bfa; }
.rd-log__msg { color: #e2e8f0; word-break: break-word; }

.rd-empty {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #64748b;
  font-size: 0.95rem;
}

.rd-empty i {
  font-size: 2rem;
  color: #94a3b8;
}

@media (max-width: 768px) {
  .rd-log__line {
    grid-template-columns: 1fr;
    gap: 2px;
    margin-bottom: 8px;
  }
}
</style>
